import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import Overlay from "components/Overlay/Overlay";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import { createVideo, setVideoPublished, updateVideo } from "services/videoLibraryApi";
import { normalizeSynthesiaEmbedUrl } from "utils/synthesiaEmbedUrl";
import {
  formatTimestamp,
  parseTimestampInput,
} from "helpers/publicVideoComments";

const MAX_TITLE_LENGTH = 160;
const MAX_DESCRIPTION_LENGTH = 5000;

const VISIBILITY_OPTIONS = [
  {
    value: "internal",
    label: "Internal Only",
    hint: "Signed-in portal users only. The link still works, but it asks them to sign in.",
  },
  {
    value: "public",
    label: "Public",
    hint: "Anyone with the link can watch it. Comments require a name and email.",
  },
];

// Publish state. A video starts as Draft; the two states after that are the ones
// the publish / unpublish endpoints can set.
const STATUS_OPTIONS = [
  {
    value: "draft",
    label: "Draft",
    hint: "Not published. Its link does not work yet.",
  },
  {
    value: "published",
    label: "Published",
    hint: "The link works for whoever is allowed to watch it.",
  },
  {
    value: "unpublished",
    label: "Unpublished",
    hint: "Temporarily off. The link stops working until it is published again.",
  },
];

// Extra languages beyond the English root. Code -> display label.
const LANGUAGE_OPTIONS = [
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "pt", label: "Portuguese" },
  { code: "it", label: "Italian" },
  { code: "ar", label: "Arabic" },
  { code: "zh", label: "Chinese" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "ru", label: "Russian" },
  { code: "tr", label: "Turkish" },
  { code: "nl", label: "Dutch" },
  { code: "pl", label: "Polish" },
  { code: "hi", label: "Hindi" },
  { code: "ur", label: "Urdu" },
  { code: "fa", label: "Persian" },
];

const labelForCode = (code) =>
  LANGUAGE_OPTIONS.find((option) => option.code === code)?.label ||
  code.toUpperCase();

const VideoNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, showSnackbar } = useStateContext();

  const isEditMode = Boolean(overlayData?.video?.id);
  const video = overlayData?.video;

  usePageTitle(isEditMode ? "Edit Video" : "New Video");

  const [title, setTitle] = useState(video?.title || "");
  const [description, setDescription] = useState(video?.description || "");
  const [embedInput, setEmbedInput] = useState(video?.synthesiaEmbedUrl || "");
  const [visibility, setVisibility] = useState(
    video?.visibility === "public" ? "public" : "internal",
  );
  const [status, setStatus] = useState(
    ["published", "unpublished"].includes(video?.status)
      ? video.status
      : "draft",
  );
  const [duration, setDuration] = useState(
    Number.isFinite(video?.durationSeconds)
      ? formatTimestamp(video.durationSeconds)
      : "",
  );
  const [durationError, setDurationError] = useState("");
  const [languages, setLanguages] = useState(() =>
    Array.isArray(video?.languages)
      ? video.languages.map((language) => ({
          code: language.code,
          label: language.label,
          synthesiaVideoId: language.synthesiaVideoId || "",
        }))
      : [],
  );
  const [languagesError, setLanguagesError] = useState("");
  const [saving, setSaving] = useState(false);
  const [titleError, setTitleError] = useState("");
  const [embedError, setEmbedError] = useState("");

  // Paste either the bare URL or Synthesia's full <div><iframe>… snippet.
  const normalizedEmbedUrl = useMemo(
    () => normalizeSynthesiaEmbedUrl(embedInput),
    [embedInput],
  );

  const closeOverlay = () => {
    if (saving) return;
    setOverlayName("");
  };

  const handleSave = async () => {
    if (saving) return;

    const trimmedTitle = title.trim();
    let hasError = false;

    if (!trimmedTitle) {
      setTitleError("A title is required.");
      hasError = true;
    }
    if (!normalizedEmbedUrl) {
      setEmbedError("Paste a valid Synthesia embed URL or embed code.");
      hasError = true;
    }

    let durationSeconds = null;
    if (duration.trim()) {
      const parsed = parseTimestampInput(duration);
      if (parsed === null || parsed <= 0) {
        setDurationError("Use mm:ss, e.g. 2:30.");
        hasError = true;
      } else {
        durationSeconds = parsed;
      }
    }

    if (hasError) {
      showSnackbar("Please fix the highlighted fields", theme, "error");
      return;
    }

    const payload = {
      title: trimmedTitle,
      description: description.trim(),
      synthesiaEmbedUrl: normalizedEmbedUrl,
      visibility,
      durationSeconds,
    };

    // Only send languages when the admin has actually listed some. An empty
    // array would override the server's auto-detection and wipe the detected
    // translations, so omit the field entirely instead.
    if (languages.length > 0) {
      payload.languages = languages.map((language) => ({
        code: language.code,
        label: language.label || labelForCode(language.code),
        synthesiaVideoId: language.synthesiaVideoId.trim(),
      }));
    }

    setSaving(true);
    try {
      if (isEditMode) {
        await updateVideo(video.id, payload);
        // Publish state is not part of the video payload — the API owns it through
        // the publish / unpublish endpoints, so apply it only when it changed.
        if (status === "published" && video.status !== "published") {
          await setVideoPublished(video.id, true);
        } else if (status === "unpublished" && video.status !== "unpublished") {
          await setVideoPublished(video.id, false);
        }
        showSnackbar("Video updated successfully", theme, "success");
      } else {
        const created = await createVideo(payload);
        const createdId = created?.data?.video?.id;
        if (status === "published" && createdId) {
          await setVideoPublished(createdId, true);
        }
        showSnackbar("Video created successfully", theme, "success");
      }
      setOverlayName("");
      overlayData?.onVideosSaved?.();
    } catch (error) {
      showSnackbar(
        error?.response?.data?.error || "The video could not be saved",
        theme,
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const footerActions = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        gap: 1,
      }}
    >
      <Box />
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {isMobile ? (
          <Tooltip title="Discard" placement="top">
            <IconButton
              color="primary"
              onClick={closeOverlay}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
              }}
            >
              <DisabledByDefaultRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            size="large"
            onClick={closeOverlay}
            disabled={saving}
            sx={{ borderRadius: "12px" }}
          >
            Discard
          </Button>
        )}

        {isMobile ? (
          <IconButton
            color="primary"
            onClick={handleSave}
            disabled={saving}
            sx={{
              width: "40px",
              height: "40px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.background.default,
              borderRadius: "12px",
              "&:hover": { backgroundColor: theme.palette.primary.dark },
              "&.Mui-disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
          >
            <SaveRoundedIcon />
          </IconButton>
        ) : (
          <Button
            loading={saving}
            color="primary"
            variant="contained"
            size="large"
            endIcon={<SaveRoundedIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ borderRadius: "12px" }}
          >
            Save
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Overlay
      id="video-new-edit"
      open={true}
      onClose={closeOverlay}
      title={isEditMode ? "Edit Video" : "New Video"}
      height="fit-content"
      footer={footerActions}
    >
      <Box
        sx={{
          flexGrow: 1,
          p: "1rem 2.3rem 1rem 2.5rem",
          height: "100%",
          width: "100%",
          overflowY: "auto",
        }}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Video Details
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Title"
              value={title}
              onChange={(event) => {
                setTitle(event.target.value);
                setTitleError("");
              }}
              error={Boolean(titleError)}
              helperText={titleError || undefined}
              inputProps={{ maxLength: MAX_TITLE_LENGTH }}
              placeholder="e.g., Tenfold: Handle Apartment Emergencies Safely"
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              multiline
              minRows={3}
              inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
              helperText={`${description.length} / ${MAX_DESCRIPTION_LENGTH} characters`}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Synthesia embed URL or embed code"
              value={embedInput}
              onChange={(event) => {
                setEmbedInput(event.target.value);
                setEmbedError("");
              }}
              error={Boolean(embedError)}
              helperText={
                embedError ||
                "Paste the embed URL or the full <div><iframe> code from Synthesia."
              }
              multiline
              minRows={2}
              placeholder="https://share.synthesia.io/embeds/videos/..."
              inputProps={{ spellCheck: false }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Who can watch this video?"
              value={visibility}
              onChange={(event) => setVisibility(event.target.value)}
              helperText={
                VISIBILITY_OPTIONS.find(
                  (option) => option.value === visibility,
                )?.hint
              }
            >
              {VISIBILITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              helperText={
                STATUS_OPTIONS.find((option) => option.value === status)?.hint
              }
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  // A video cannot go back to Draft once it has been published.
                  disabled={option.value === "draft" && Boolean(video?.status) && video.status !== "draft"}
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Video length"
              value={duration}
              onChange={(event) => {
                setDuration(event.target.value);
                setDurationError("");
              }}
              error={Boolean(durationError)}
              helperText={
                durationError ||
                "Optional, mm:ss. Left blank it is fetched from Synthesia (needs SYNTHESIA_API_KEY)."
              }
              placeholder="2:30"
              inputProps={{ maxLength: 12, inputMode: "numeric" }}
            />
          </Grid>

          {normalizedEmbedUrl && (
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ mb: 1, color: theme.palette.primary.text2 }}
              >
                Additional languages
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Translations are detected automatically from Synthesia and added
                here with their own video id. You only need to add a row by hand
                if a translation is named differently from
                &quot;&lt;CODE&gt; - &lt;title of the English video&gt;&quot;.
              </Typography>
              {languagesError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {languagesError}
                </Alert>
              )}
              <Stack spacing={1.5}>
                {languages.map((language, index) => (
                  <Box
                    key={`lang-${index}`}
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 1.5,
                      alignItems: { xs: "stretch", sm: "center" },
                    }}
                  >
                    <TextField
                      select
                      size="small"
                      label="Language"
                      value={language.code}
                      onChange={(event) =>
                        setLanguages((current) =>
                          current.map((entry, entryIndex) =>
                            entryIndex === index
                              ? {
                                  ...entry,
                                  code: event.target.value,
                                  label: labelForCode(event.target.value),
                                }
                              : entry,
                          ),
                        )
                      }
                      sx={{ minWidth: 170, flex: "0 0 auto", width: "auto" }}
                    >
                      {LANGUAGE_OPTIONS.map((option) => (
                        <MenuItem
                          key={option.code}
                          value={option.code}
                          disabled={languages.some(
                            (entry, entryIndex) =>
                              entryIndex !== index && entry.code === option.code,
                          )}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label="Video ID (optional)"
                      value={language.synthesiaVideoId}
                      onChange={(event) =>
                        setLanguages((current) =>
                          current.map((entry, entryIndex) =>
                            entryIndex === index
                              ? { ...entry, synthesiaVideoId: event.target.value }
                              : entry,
                          ),
                        )
                      }
                      placeholder="Blank = same video as English"
                      inputProps={{ spellCheck: false }}
                      sx={{ flex: "1 1 auto", minWidth: 0, width: "auto" }}
                    />
                    <IconButton
                      onClick={() =>
                        setLanguages((current) =>
                          current.filter((_, entryIndex) => entryIndex !== index),
                        )
                      }
                      aria-label={`Remove ${labelForCode(language.code)}`}
                      sx={{ color: theme.palette.error.main }}
                    >
                      <DeleteRoundedIcon />
                    </IconButton>
                  </Box>
                ))}
                <Box>
                  <Button
                    startIcon={<AddRoundedIcon />}
                    onClick={() => {
                      const next = LANGUAGE_OPTIONS.find(
                        (option) =>
                          !languages.some((entry) => entry.code === option.code),
                      );
                      if (!next) {
                        setLanguagesError("All available languages are already added.");
                        return;
                      }
                      setLanguagesError("");
                      setLanguages((current) => [
                        ...current,
                        { code: next.code, label: next.label, synthesiaVideoId: "" },
                      ]);
                    }}
                    sx={{ borderRadius: "12px", textTransform: "none" }}
                  >
                    Add language
                  </Button>
                </Box>
              </Stack>
            </Grid>
          )}

          {!normalizedEmbedUrl && embedInput.trim() !== "" && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="warning">
                This does not look like a Synthesia embed link yet.
              </Alert>
            </Grid>
          )}

          {normalizedEmbedUrl && (
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ mb: 1, color: theme.palette.primary.text2 }}
              >
                Preview
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  aspectRatio: "16 / 9",
                  borderRadius: "12px",
                  bgcolor: "common.black",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <iframe
                  src={normalizedEmbedUrl}
                  title="Synthesia video preview"
                  allow="encrypted-media; fullscreen; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Overlay>
  );
};

export default VideoNewEdit;
